import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  TestAssignment,
  CreateTestAssignmentDto,
} from '../../interfaces/test-assignment';
import {
  ApiCollectionResponse,
  ApiSimpleResponse,
  ApiSingleResponse,
} from '../../interfaces/responses/api-response';

/**
 * Professor-side endpoints for test assignments (devoirs / examens affectés).
 * Auto-scoped via JWT — no professorId path param needed.
 *
 * Lifecycle :  draft → active (PATCH /activate) → closed (PATCH /close)
 */
@Injectable({ providedIn: 'root' })
export class ProfessorTestAssignmentService {
  private readonly base = `${environment.BASE_URL}/professor/test-assignments`;

  constructor(private http: HttpClient) {}

  /** All assignments created by the connected professor. */
  myAssignments(): Observable<ApiCollectionResponse<TestAssignment>> {
    return this.http.get<ApiCollectionResponse<TestAssignment>>(this.base);
  }

  getById(id: number): Observable<ApiSingleResponse<TestAssignment>> {
    return this.http.get<ApiSingleResponse<TestAssignment>>(
      `${this.base}/${id}`,
    );
  }

  create(
    payload: CreateTestAssignmentDto,
  ): Observable<ApiSingleResponse<TestAssignment>> {
    return this.http.post<ApiSingleResponse<TestAssignment>>(this.base, payload);
  }

  update(
    id: number,
    payload: CreateTestAssignmentDto,
  ): Observable<ApiSingleResponse<TestAssignment>> {
    return this.http.put<ApiSingleResponse<TestAssignment>>(
      `${this.base}/${id}`,
      payload,
    );
  }

  delete(id: number): Observable<ApiSimpleResponse> {
    return this.http.delete<ApiSimpleResponse>(`${this.base}/${id}`);
  }

  activate(id: number): Observable<ApiSingleResponse<TestAssignment>> {
    return this.http.patch<ApiSingleResponse<TestAssignment>>(
      `${this.base}/${id}/activate`,
      null,
    );
  }

  close(id: number): Observable<ApiSingleResponse<TestAssignment>> {
    return this.http.patch<ApiSingleResponse<TestAssignment>>(
      `${this.base}/${id}/close`,
      null,
    );
  }

  byGroup(groupId: number): Observable<ApiCollectionResponse<TestAssignment>> {
    return this.http.get<ApiCollectionResponse<TestAssignment>>(
      `${this.base}/by-group/${groupId}`,
    );
  }

  byStudent(
    studentId: number,
  ): Observable<ApiCollectionResponse<TestAssignment>> {
    return this.http.get<ApiCollectionResponse<TestAssignment>>(
      `${this.base}/by-student/${studentId}`,
    );
  }

  bySubject(
    subjectId: number,
  ): Observable<ApiCollectionResponse<TestAssignment>> {
    return this.http.get<ApiCollectionResponse<TestAssignment>>(
      `${this.base}/by-subject/${subjectId}`,
    );
  }
}
